import type {HideableValue} from '@wix/devzai-utils-common';
import {evaluateFunction, hideableValueCreateHidden, Values} from '@wix/devzai-utils-common';

/**
 * Layout strategy for rendering a list of attributes.
 *
 * - `Row` renders attributes inline, separated by a middle dot (matching the Figma reference).
 * - `Column` stacks attributes vertically with an optional prefix marking per item.
 * - `Grid` lays attributes out on a multi-column grid with an optional prefix marking per item.
 */
export const DoppeActionAttributesLayoutType = {
    Row: 'Row',
    Column: 'Column',
    Grid: 'Grid',
} as const;

export type DoppeActionAttributesLayoutType = Values<typeof DoppeActionAttributesLayoutType>;

/**
 * Optional leading marker for each attribute in Column / Grid layouts.
 * The Row layout ignores this setting because attributes are visually
 * delimited by the inline separator instead of per-item prefixes.
 */
export const DoppeActionAttributesPrefixMarking = {
    None: 'None',
    Checkmark: 'Checkmark',
    Bullets: 'Bullets',
    Numbers: 'Numbers',
} as const;

export type DoppeActionAttributesPrefixMarking = Values<typeof DoppeActionAttributesPrefixMarking>;

/**
 * Per-layout settings — extra fields that only make sense for some layout types.
 * Discriminated by `type`; switch on it without a `default` to keep exhaustiveness checking
 * working when new layout types are added.
 */
export type DoppeActionAttributesLayoutSettings =
    | DoppeActionAttributesLayoutSettings.Row
    | DoppeActionAttributesLayoutSettings.Column
    | DoppeActionAttributesLayoutSettings.Grid;

export namespace DoppeActionAttributesLayoutSettings {

    interface Base<TYPE extends DoppeActionAttributesLayoutType> {
        type: TYPE;
    }

    export interface Row extends Base<typeof DoppeActionAttributesLayoutType.Row> {
    }

    export interface Column extends Base<typeof DoppeActionAttributesLayoutType.Column> {
        prefixMarking: DoppeActionAttributesPrefixMarking;
    }

    export interface Grid extends Base<typeof DoppeActionAttributesLayoutType.Grid> {
        prefixMarking: DoppeActionAttributesPrefixMarking;
    }
}

/**
 * A single attribute entry. Modeled as an object (not a bare string) so future per-item
 * fields (icon, link, etc.) can be added without a data migration.
 */
export interface DoppeActionAttribute {
    text: string;
}

/**
 * Common, reusable "attributes" settings shape that can be embedded in any action type
 * that wants to expose a hideable list of short factual phrases (e.g., "1:33 h", "English", "Video").
 *
 * The whole object is wrapped in `HideableValue` so consumers can toggle visibility from the
 * settings panel without losing the user's items.
 */
export type DoppeActionAttributesSettings = HideableValue<{
    attributes: DoppeActionAttribute[];
    layoutSettings: DoppeActionAttributesLayoutSettings;
}>;

export function doppeActionAttributesLayoutSettingsGetDefaultSettings(
    type: DoppeActionAttributesLayoutType
): DoppeActionAttributesLayoutSettings {
    return evaluateFunction((): DoppeActionAttributesLayoutSettings => {
        switch (type) {
            case DoppeActionAttributesLayoutType.Row: {
                return {
                    type: DoppeActionAttributesLayoutType.Row,
                };
            }
            case DoppeActionAttributesLayoutType.Column: {
                return {
                    type: DoppeActionAttributesLayoutType.Column,
                    prefixMarking: DoppeActionAttributesPrefixMarking.Bullets,
                };
            }
            case DoppeActionAttributesLayoutType.Grid: {
                return {
                    type: DoppeActionAttributesLayoutType.Grid,
                    prefixMarking: DoppeActionAttributesPrefixMarking.Bullets,
                };
            }
        }
    });
}

/**
 * Default `DoppeActionAttributesSettings` — hidden by default so existing actions that
 * adopt this field stay visually unchanged. Once made visible by the user, the inner
 * value starts with an empty `attributes` list and the Row layout.
 */
export function doppeActionAttributesSettingsCreateDefault(): DoppeActionAttributesSettings {
    return hideableValueCreateHidden({
        attributes: [],
        layoutSettings: doppeActionAttributesLayoutSettingsGetDefaultSettings(
            DoppeActionAttributesLayoutType.Row
        ),
    });
}

/**
 * Migration helper for stored values that may be missing fields after a schema bump.
 * Tolerates `undefined`, partially-shaped objects, and missing nested `layoutSettings`.
 */
export function doppeActionAttributesSettingsDefaultValuesMigration(
    value: DoppeActionAttributesSettings | undefined
): DoppeActionAttributesSettings {
    if (value === undefined) {
        return doppeActionAttributesSettingsCreateDefault();
    }

    return value;
}
